import { OnInit, Component, ViewChild } from "@angular/core";
import { AuthService, UserPermissions } from "../../services/auth.service";
import { ReportService } from "../../services/report.service";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { LoadingSpinnerService } from "../../components/loading-spinner/loading-spinner.service";
import { ToastService } from "../../components/toast/toast.service";
import { DataDictionaryService } from "../../services/data-dictionary.service";
import * as _ from "underscore";
import { LowyGridComponent } from "../../components/grid/grid.component";
import { ReportColumnsModalComponent } from "./report-columns-modal.component";

@Component({
  selector: "lowy-report-builder",
  templateUrl: "./report-builder.component.html",
  styleUrls: ["./report-builder.component.scss"],
  providers: [AuthService, ReportService, DataDictionaryService],
})
export class ReportBuilderComponent implements OnInit {
  public reportBuilderForm: FormGroup;
  public downloadConsolidatedForm: FormGroup;
  public reportTypes: Array<any>;
  public allTopLevelSheetIds: Array<string>;
  public userPermissions: UserPermissions;
  public adminReportData: any[];
  public showAdminReport = false;
  public selectedReportType = "Grading";

  public allImageCols: string[] = [];
  public allV4Cols: string[] = [];
  public selectedImageCols: string[] = [];
  public selectedV4Cols: string[] = [];

  @ViewChild("nhorV4Grid") nhorV4Grid: LowyGridComponent;
  @ViewChild("reportColumnsModal")
  reportColumnsModal: ReportColumnsModalComponent;

  constructor(
    private authService: AuthService,
    private reportService: ReportService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private loadingSpinnerService: LoadingSpinnerService,
    private dataDictionaryService: DataDictionaryService
  ) {
    this.reportBuilderForm = this.formBuilder.group({
      fromDate: ["", Validators.required],
      toDate: ["", Validators.required],
      reportType: ["", Validators.required],
    });
    this.downloadConsolidatedForm = this.formBuilder.group({
      topLevelSheetId: ["", Validators.required],
    });

    this.reportTypes = ["CF-FFA", "FAF", "OCT", "Diagnosis"];
  }

  async ngOnInit() {
    this.authService.permissions$.subscribe((permissions) => {
      this.userPermissions = permissions;
    });

    this.loadingSpinnerService.start();
    this.allTopLevelSheetIds = _.chain(
      await this.dataDictionaryService.getTopLevelDataMappings()
    )
      .pluck("topLevelSpreadsheetId")
      .uniq()
      .sort()
      .value();
    this.loadingSpinnerService.stop();
  }

  public selectReport(reportType: string) {
    this.selectedReportType = reportType;
    if (reportType === "NHOR V4") {
      if (this.adminReportData) {
        // If data already exists, just re-render the grid.
        this.updateGrid();
      } else {
        // Otherwise, fetch the data for the first time.
        this.getAdminNhorV4Report();
      }
    }
  }

  public async getAdminNhorV4Report() {
    this.loadingSpinnerService.start();
    try {
      const data = await this.reportService.getAdminNhorV4Report();

      // Ensure all data values are strings or null to prevent pipe errors
      this.adminReportData = data.report.map((row) => {
        const newRow = {};
        for (const key in row) {
          if (row.hasOwnProperty(key)) {
            newRow[key] = row[key] === null ? null : String(row[key]);
          }
        }
        return newRow;
      });

      this.allImageCols = data.summary.imageCols || [];
      this.allV4Cols = data.summary.v4Cols || [];
      this.selectedImageCols = data.summary.defaultImageCols || [];
      this.selectedV4Cols = data.summary.defaultV4Cols || [];

      this.showAdminReport = true;
    } catch (err) {
      this.toastService.throwToast(err.message);
    } finally {
      this.loadingSpinnerService.stop();
      // Update grid after loading is false and view is stable
      this.updateGrid();
    }
  }

  public openColumnsModal() {
    this.reportColumnsModal.open(this.selectedImageCols, this.selectedV4Cols);
  }

  public updateSelectedColumns(newColumns: {
    imageCols: string[];
    v4Cols: string[];
  }) {
    this.selectedImageCols = newColumns.imageCols;
    this.selectedV4Cols = newColumns.v4Cols;
    this.updateGrid();
  }

  private updateGrid(attempt = 0) {
    // Defer grid interaction to allow view to stabilize
    setTimeout(() => {
      if (this.nhorV4Grid) {
        const displayCols = [
          "checkbox",
          "patientId",
          ...this.selectedV4Cols,
          ...this.selectedImageCols,
        ];
        const displayHeaders = [
          "checkbox",
          "Patient ID",
          ...this.selectedV4Cols,
          ...this.selectedImageCols,
        ];

        this.nhorV4Grid.dataReady(
          this.adminReportData,
          displayCols,
          displayHeaders
        );
      } else if (attempt < 5) {
        // If grid isn't ready, retry a few times.
        this.updateGrid(attempt + 1);
      }
    }, 100);
  }

  public async buildReport() {
    if (this.reportBuilderForm.valid) {
      try {
        this.loadingSpinnerService.start();
        const downloadLink = await this.reportService.buildReport(
          this.reportBuilderForm.value
        );
        this.loadingSpinnerService.stop();
        window.location.assign(downloadLink);
      } catch (err) {
        this.loadingSpinnerService.stop();
        this.toastService.throwToast(err);
      }
    }
  }

  public async downloadConsolidatedSheet() {
    if (this.downloadConsolidatedForm.valid) {
      try {
        this.loadingSpinnerService.start();
        const downloadLink = await this.reportService.downloadConsolidatedSheet(
          this.downloadConsolidatedForm.value.topLevelSheetId
        );
        this.loadingSpinnerService.stop();
        this.toastService.throwToast(
          "Report requested. Please check your email for a download link. May take up to 20 minutes."
        );
      } catch (err) {
        this.loadingSpinnerService.stop();
        this.toastService.throwToast(err);
      }
    }
  }

  public downloadAllV4Report(downloadType: string) {
    this.nhorV4Grid.downloadFiles(downloadType, this.adminReportData);
  }
}

export class ReportSettings {
  fromDate: string;
  toDate: string;
  reportType: string;
}



// WEBPACK FOOTER //
// ./src/app/home/report-builder/report-builder.component.ts